import { FC, Fragment, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import CommentParticipants from '@components/Message/CommentParticipants';
import Buttons from '@components/Buttons';
import CaretCircleDown from '@components/Icons/CaretCircleDown';
import { UserWorkflowInvoice } from '@noodleApi/ApiDefinition/workflows';
import Spacer from '@components/Spacer';
import * as format from '@format';
import InvoiceCard from '@components/InvoiceCard';
import { useUser } from '@hooks';
import AssignCollaboratorsToAttachmentModal from '@modals/AssignCollaboratorsToAttachmentModal';
import { INVOICE_STATUS } from '@typings/api-models';
import useUserWorkflowContext from '@/hooks/useUserWorkflowContext';
import classes from './UserWorkflowInvoices.module.scss';

const UserWorkflowInvoices: FC<{
  canAssignCollaborators: boolean;
  creator: Pick<ApiModels.Creator, 'slug' | 'personId'>;
  invoices: UserWorkflowInvoice[];
  isCreatorView: boolean;
  userWorkflowId: string;
  onChange: () => Promise<void>;
}> = ({ canAssignCollaborators, creator, invoices, isCreatorView, userWorkflowId, onChange }) => {
  const [user] = useUser();
  const { collaborators } = useUserWorkflowContext();

  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(null);
  const [isAssignCollaboratorModalOpen, setAssignCollaboratorModalOpen] = useState(false);

  const totalAmountPaid = invoices.reduce((acc, val) => acc + val.amountPaid, 0) || 0;
  const totalAmountDue = invoices.reduce((acc, val) =>
    ([INVOICE_STATUS.VOIDED, INVOICE_STATUS.SKIPPED].includes(val.status) ? acc : acc + val.amountDue), 0) || 0;
  const totalAmount = invoices.reduce((acc, val) => acc + val.amountTotal, 0) || 0;

  const onAssignCollaborators = async (): Promise<void> => {
    setAssignCollaboratorModalOpen(false);
    await onChange();
  };

  const notVoidedInvoices = invoices.filter(invoice => invoice.status !== INVOICE_STATUS.VOIDED);

  if (notVoidedInvoices.length === 0 && !isCreatorView) {
    return <Spacer size={8} />;
  }

  if (notVoidedInvoices.length === 0 && isCreatorView) {
    return null;
  }

  return (
    <>
      {!isCreatorView && <Spacer size={8} />}
      <div className={isCreatorView ? classes.wrapper : classes.userProfileWrapper}>
        <div className={classes.list}>
          {isAssignCollaboratorModalOpen && currentInvoiceId && (
            <AssignCollaboratorsToAttachmentModal
              attachmentType={ApiModels.MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE}
              attachmentReferenceId={currentInvoiceId}
              collaborators={collaborators}
              onClose={() => setAssignCollaboratorModalOpen(false)}
              onSubmit={onAssignCollaborators}
              userWorkflowId={userWorkflowId}
            />
          )}
          {notVoidedInvoices.length > 0 && <div className={classes.invoices}>
            <details open={!isCreatorView} className={isCreatorView ? classes.details : classes.userProfileDetails}>
              <summary>
                <div>
                  <h2>
                    Invoices <small>{notVoidedInvoices.length}</small>
                  </h2>
                  <p className="caption">Total Remaining Balance: {format.price.withCurrency(totalAmountDue, invoices[0].currency)}</p>
                  <progress value={totalAmountPaid} max={totalAmount} className={classes.progress} />
                </div>
                <CaretCircleDown size={16} weight="fill" />
              </summary>
              {notVoidedInvoices.map(invoice => {
                const assignedToYou = user && invoice.assigneeIds.includes(user.id);
                return (
                  <Fragment key={invoice.id}>
                    <Spacer size={12} />
                    {assignedToYou && (
                      <div
                        className={classes.assigned}
                      >
                        <b>You are assigned</b>
                      </div>
                    )}
                    <div className={classes.invoice__card} style={assignedToYou
                      ? {
                        border: '4px solid var(--color-warning)',
                        borderRadius: '0 0 12px 12px',
                      }
                      : undefined}>
                      <InvoiceCard
                        canClick={invoice.canAccess}
                        canSkip={false}
                        invoice={invoice}
                        creatorSlug={creator.slug}
                        reload={onChange}
                        completedAt={invoice.completedAt}
                      />
                      {(collaborators.filter(c => invoice.assigneeIds.includes(c.id)).length > 0 || canAssignCollaborators) && (
                        <div className={classes.invoice__card__assignments}>
                          <div>
                            <CommentParticipants
                              userLogoSize={20}
                              participantsToHighlight={invoice.assigneeIds?.filter(id => id === user?.id)}
                              remainingParticipantsAsNumber
                              participants={collaborators.filter(c => invoice.assigneeIds?.includes(c.id))}
                              maxLength={2}
                              areAssignees
                            />
                          </div>
                          {canAssignCollaborators && (
                            <Buttons
                              isWrapper
                              onClick={() => {
                                setAssignCollaboratorModalOpen(true);
                                setCurrentInvoiceId(invoice.id);
                              }}
                            >
                              {invoice.assigneeIds?.includes(user?.id || '')
                                ? (
                                  <>Reassign</>
                                )
                                : (
                                  <>Assign {collaborators.filter(c => invoice.assigneeIds?.includes(c.id)).length === 0 ? 'collaborators' : ''}</>
                                )}
                            </Buttons>
                          )}
                        </div>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </details>
          </div>}
        </div >
      </div>
      {!isCreatorView && <Spacer />}
    </>
  );
};

export default UserWorkflowInvoices;
